import React, { useState, useRef } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useNavigate } from "react-router-dom";
import CustomMouseWheelAction from "./CustomNode/CustomMouseWheelAction";
import { Action, InputType } from '@projectstorm/react-canvas-core';
import WaveXchangeIcon from "../../../assets/images/wavexchange-small-icon.svg";
import AddBandwidthCanvasIcon from "../../../assets/images/add-bandwidth-canvas.svg";
import AddExistingNrCanvasIcon from "../../../assets/images/add-existing-nr-canvas.svg";
import ManagePortIcon from "../../../assets/images/manage-port.svg";
import ManageLinkIcon from "../../../assets/images/manage-bandwidth.svg";
import AddPortIcon from "../../../assets/images/add-port.svg";
import AddBandwidthIcon from "../../../assets/images/add-bandwidth.svg";
import { DiagramEngine } from "@projectstorm/react-diagrams";
import DeleteIcon from "../../../assets/images/delete.svg";
import HideIcon from "../../../assets/images/hide.svg";
import AWSIcon from "../../../assets/images/aws.svg";
import GlobeIcon from "../../../assets/images/globe.svg";
import Icon from "../../../assets/images/icon.svg";
import { AuthRoutes, Roles } from "../../../enums";
import AuthService from "../../../services/auth.service";
import { NODE_CONSTANTS } from "../../../utils/appConstants";
import BasicMenu from "../../DesignSystem/menu";
import { Icon as Iconify } from "@iconify/react";
import createEngine, {
  DefaultPortModel,
  DefaultDiagramState,
  DefaultNodeModel,
  PathFindingLinkFactory,
  LinkModel,
  LinkModelGenerics,
} from "@projectstorm/react-diagrams";
import ZoomFunction from "../CanvasController/ZoomFunction";

interface NENodeOperationsProps {
  engine: DiagramEngine;
  ports: any[];
  groups: any[];
  selectedNe: any;
  onDeleteClick: (_: any) => void;
  setWidth: (_: any) => void;
  setHeight: (_: any) => void;
  resetPortPosition: () => void;
  canvasWidth: number;
  canvasHeight: number;
  nodeWidth: number;
  nodeHeight: number;
  nodePositionX: number;
  nodePositionY: number;
  isMaximized: boolean;
  setIsMaximized: (value: boolean) => void;
}

const NENodeOperations: React.FC<NENodeOperationsProps> = ({
  engine, ports, groups, selectedNe, onDeleteClick, setWidth, setHeight,
  resetPortPosition, canvasWidth, canvasHeight, nodeWidth, nodeHeight, nodePositionX, nodePositionY, isMaximized, setIsMaximized 
}) => {
  const navigate = useNavigate();
  const displaceOffset = 4000;
  const canvas = engine.getCanvas();
  
  const [isMaxNodeSelected, setIsMaxNodeSelected] = useState(false);
  const [isShrink300Selected, setIsShrink300Selected] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  let dragTimeout: NodeJS.Timeout;

  const savedMouseWheelAction = useRef<CustomMouseWheelAction | null>(null);
  const disableMouseWheelAction = () => {
    const actionEventBus = engine.getActionEventBus();
    const mouseWheelAction = actionEventBus.getActionsForType(InputType.MOUSE_WHEEL)
      .find(action => action instanceof CustomMouseWheelAction);
    
    if (mouseWheelAction) {
      savedMouseWheelAction.current = mouseWheelAction as CustomMouseWheelAction;
      actionEventBus.deregisterAction(mouseWheelAction);
    }
  };

  const enableMouseWheelAction = () => {
    if (savedMouseWheelAction.current) {
      const actionEventBus = engine.getActionEventBus();
      actionEventBus.registerAction(savedMouseWheelAction.current);
      savedMouseWheelAction.current = null;
    }
  };

  const handleMaxNodeClick = async () => {
    const model = engine.getModel();
    const zoomLevel = 0.7;
    
    model.getNodes().forEach((nodeStep: any) => {
      const position = nodeStep.getPosition();
      const { x, y } = position;
      
      let newX;
      if (x > nodePositionX) {
        newX = x + displaceOffset;
        nodeStep.setPosition(newX, y);
      } else if (x < nodePositionX) {
        newX = x - displaceOffset;
        nodeStep.setPosition(newX, y);
      }
    });
    
    model.setOffsetX(-(nodePositionX - 20) * zoomLevel);
    model.setOffsetY(-(nodePositionY - 61) * zoomLevel);
                
    const maximizeNodeHeight = ((canvasHeight - 60.99 - 40) / zoomLevel - nodeHeight);
    const maximizeNodeWidth = ((canvasWidth - 260) / zoomLevel - nodeWidth);
    
    setHeight(maximizeNodeHeight);
    setWidth(maximizeNodeWidth);
    
    model.setZoomLevel(zoomLevel * 100);
    engine.repaintCanvas();
    
    setIsMaxNodeSelected(true);  // Disable Max Node button
    setIsShrink300Selected(false);  // Ensure Shrink 300 is enabled
    disableMouseWheelAction();
    window.dispatchEvent(new Event ('disableZoom'));
    setIsMaximized(true);
  };

  const handleShrink300Click = () => {
    setHeight(300 - nodeHeight);
    setWidth(300 - nodeWidth);

    const model = engine.getModel();
    model.getNodes().forEach((nodeStep: any) => {
      const position = nodeStep.getPosition();
      const { x, y } = position;
      let newX;
      if (x > nodePositionX) {
        newX = x - displaceOffset;
        nodeStep.setPosition(newX, y);
      } else if (x < nodePositionX) {
        newX = x + displaceOffset;
        nodeStep.setPosition(newX, y);
      }
    });
    engine.repaintCanvas();

    setIsShrink300Selected(true);  // Enable Max Node button again
    setIsMaxNodeSelected(false);  // Reset Max Node selection
    enableMouseWheelAction();
    setIsMaximized(false);
    window.dispatchEvent(new Event ('enableZoom'))
  };

  const expandButton = selectedNe.public === 0 && !isMaxNodeSelected ? (
    <Tooltip title="Expand Node">
      <div onClick={handleMaxNodeClick} style={{ cursor: 'pointer', marginRight: '5px' }}>
        <Iconify icon={"lucide:expand"} width={35} color="white" />
      </div>
    </Tooltip>
  ) : null;

  const shrinkButton = selectedNe.public === 0 && isMaxNodeSelected ? (
    <Tooltip title="Shrink Node">
      <div onClick={handleShrink300Click} style={{ cursor: 'pointer', marginRight: '5px' }}>
        <Iconify icon={"lucide:shrink"} width={35} color="white" />
      </div>
    </Tooltip>
  ) : null;

  const menuItems = [
    {
      content: (
        <div>
          <Iconify icon={"lucide:shrink"} width={20} color="white" />
          <span style={{ paddingLeft: "5px" }}>Shrink Node</span>
        </div>
      ),
      action: () => {
        setHeight(-100);
        setWidth(-100);
      },
      disabled: isMaxNodeSelected, // Disabled when 'Max Node' is selected
    },
    {
      content: (
        <div>
          <Iconify icon={"lucide:expand"} width={20} color="white" />
          <span style={{ paddingLeft: "5px" }}>Expand Node</span>
        </div>
      ),
      action: () => {
        setHeight(+100);
        setWidth(+100);
      },
      disabled: isMaxNodeSelected, // Disabled when 'Max Node' is selected
    },
    {
      content: (
        <div>
          <Iconify icon={"tabler:replace"} width={20} color="white" />
          <span style={{ paddingLeft: "5px" }}>Reset Ports</span>
        </div>
      ),
      action: () => resetPortPosition(),
    },
    {
      content: (
        <div>
          <Iconify icon={"mdi:hide"} width={20} color="white" />
          <span style={{ paddingLeft: "5px" }}>Hide Node</span>
        </div>
      ),
      action: () => onDeleteClick(selectedNe),
    },
    {
      content: (
        <div>
          <img style={{ width: "20px", height: "20px" }} src={ManagePortIcon} alt="Manage Ports" />
          <span style={{ paddingLeft: "5px" }}>Manage Ports</span>
        </div>
      ),
      action: () => navigate(AuthRoutes.managePorts, { state: selectedNe }),
    },
    {
      content: (
        <div>
          <img style={{ width: "20px", height: "20px" }} src={ManageLinkIcon} alt="Manage Links" />
          <span style={{ paddingLeft: "5px" }}>Manage Links</span>
        </div>
      ),
      action: () => navigate(AuthRoutes.manageBandwidth, { state: selectedNe }),
    },
    {
      content: (
        <div>
          <img style={{ width: "20px", height: "20px" }} src={AddPortIcon} alt="Manage Links" />
          <span style={{ paddingLeft: "5px" }}>Add Ports</span>
        </div>
      ),
      action: () => navigate(AuthRoutes.addPort, { state: selectedNe }),
    },
    {
      content: (
        <div>
          <img style={{ width: "20px", height: "20px" }} src={AddBandwidthIcon} alt="Manage Links" />
          <span style={{ paddingLeft: "5px" }}>Add Links</span>
        </div>
      ),
      action: () => navigate(AuthRoutes.addBandwidth, { state: { nearendNe: selectedNe, farendNe: null } }),
    },
  ];

  if (selectedNe.public !== 1) {
    menuItems.push({
      content: (
        <div>
          <img style={{ width: "20px", height: "20px" }} src={AddBandwidthIcon} alt="Manage Links" />
          <span style={{ paddingLeft: "5px" }}>Host A Private Node</span>
        </div>
      ),
      action: () => navigate(AuthRoutes.privateNode)
    });
  }

  const handleDragStart = () => {
    setIsDragging(true);
  };
  const handleDragEnd = () => {
    if (isDragging) {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    }
    dragTimeout = setTimeout(() => {
      const event = new CustomEvent("nodeDragComplete", {
        detail: { nodeId: selectedNe.ne_name },
      });
      window.dispatchEvent(event);
      setIsDragging(false);
    }, 10);
  };

  const content = (
    <div className="un-hovered-root"
    onMouseDown={handleDragStart}
    onMouseUp={handleDragEnd}
    onMouseLeave={handleDragEnd}
    onMouseOver={handleDragEnd}>
      <div>
        {selectedNe.on_net ? <img src={WaveXchangeIcon} alt="" width={25} /> : ""}
        <span>{selectedNe.ne_name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {expandButton}
        {shrinkButton}
        <BasicMenu
          content={<Iconify icon={"fluent:settings-16-regular"} color="white" width={40} />}
          menuItems={menuItems}
        />
      </div>
    </div>
  );

  return content;
};

export default NENodeOperations;